// CHAIN'S RULES, for the rebuilt screen (UI rebuild batch 2, screens/v2/Chain.tsx). Every rule here is the old screen's
// (screens/ChainActivity.tsx), copied — not re-derived — because that file exports only noteParts and the old screen
// stays what the console draws with batch 2 off. A change to one is a change to both.
import type { ChainRow } from '../../../lib/api';
import type { PillTone } from '../../Pill';
import { noteParts } from '../../../screens/ChainActivity';
import { fmtAmount } from '../../../lib/format';
import { brandCase, dustFor, railName } from '../../../lib/naming';

export const TABS = ['pending', 'auto', 'manual', 'ignored', 'all'] as const;
export type ChainTab = (typeof TABS)[number];
/** The states in Astra's words (the select and the pill): Unmatched is the BFF's `pending`. Auto and by-hand stay apart —
 *  the route reads one state at a time, and which matched by itself is the operator's own distinction (22 Aug). */
export const TAB_WORD: Record<ChainTab, string> = { pending: 'Unmatched', auto: 'Auto-matched', manual: 'Matched by hand', ignored: 'Ignored', all: 'All' };

/** THE V3 OPENING — the same instant bff/src/routes/chain.ts refuses to tie across (ChainActivity V3_OPEN_MS). */
export const V3_OPEN_MS = Date.parse('2026-08-31T20:00:00Z');   // 2026-09-01 00:00 Dubai

/** Both sides of a tie and the look-alike's owner, read off the row until lib/api.ts declares them (ChainActivity TieRow). */
export type TieRow = ChainRow & { leg_amount?: number | null; chain_sum?: number | null; leg_kind?: string | null; lookalike_of?: string | null };
/** What PATCH /api/chain/:id answers for a hand match. */
export type MarkResult = { ok: true; id: number; status: string; leg_amount?: number | null; leg_kind?: string | null; chain_sum?: number | null; delta?: number | null;
  /** The hand tie's own "more tied than the entry holds", and per posting (Mechanisms 40a503e1). */
  over_tied?: string | null; ties?: Array<{ ledger_txn_id: number; reference?: string | null; over_tied?: string | null }> };

/** WHAT A HAND TIE LEFT TIED ABOVE ITS ENTRIES, one line per posting that is over (his 100 + 502,900.0021 USDC tied to one
 *  502,900.0021 receipt, 4 Nov 2026): the server's words, the posting's reference first when the send was split. */
export function overTiedLines(d: Pick<MarkResult, 'over_tied' | 'ties'>): string[] {
  const per = (d.ties ?? []).filter((t) => t.over_tied);
  if (per.length > 1) return per.map((t) => `${t.reference ?? `tx ${t.ledger_txn_id}`}: ${t.over_tied}`);
  const one = d.over_tied ?? per[0]?.over_tied;
  return one ? [one] : [];
}
export type MarkVars = { id: number; action: 'match' | 'ignore' | 'reopen'; ledger_txn_id?: number; ties?: Array<{ ledger_txn_id: number }>; note?: string };
/** One mutation serves every row; `variables` says which row the last refusal belongs to. */
export type Mark = { mutate: (v: MarkVars) => void; isPending: boolean; isError: boolean; error: unknown; variables?: { id: number } };

/** GET /api/chain/txn/:txn as the BFF sends it (party_slug / party_name; lib/api.ts's type names `party`). */
export type ChainTxn = {
  txn: number; timestamp: string | null; reference: string | null; kind: string | null;
  wallet_slug: string | null; direction: 'in' | 'out' | null; asset: string | null; amount: number | null;
  party?: string | null; party_slug?: string | null; party_name?: string | null;
};

/** The sync's `spoofed` / `spoofed_prev`, not declared by lib/api.ts yet (ChainActivity spoofedCount / spoofedPrev). */
export function spoofedCount(sync: unknown): number {
  const n = (sync as { spoofed?: unknown }).spoofed;
  return typeof n === 'number' ? n : 0;
}
export function spoofedPrev(sync: unknown): number | null {
  const n = (sync as { spoofed_prev?: unknown }).spoofed_prev;
  return typeof n === 'number' ? n : null;
}

/** "chain sent …, trade says … — correct the trade?" when the two sides of a tie differ by more than dust (ChainActivity tieWords). */
export function tieWords(t: { leg_amount?: number | null; chain_sum?: number | null; leg_kind?: string | null; asset: string }): string | null {
  if (t.leg_amount == null || t.chain_sum == null) return null;
  if (Math.abs(t.leg_amount - t.chain_sum) <= dustFor(t.asset)) return null;
  const trade = t.leg_kind === 'trade';
  return `chain sent ${fmtAmount(t.chain_sum, t.asset)}, ${trade ? 'trade says' : 'books say'} ${fmtAmount(t.leg_amount, t.asset)} — correct the ${trade ? 'trade' : 'entry'}?`;
}

/** Ignore, as chips: the note posted is the whole sentence (ChainActivity IGNORE_REASONS — the words are posted; untouched). */
export const IGNORE_REASONS: ReadonlyArray<{ label: string; note: string }> = [
  { label: 'Pre-cutover',          note: 'Pre-cutover — inside the 1 September opening balance; nothing in these books to tie it to' },
  { label: 'Backlog ruling',       note: 'Backlog — operator ruling 2026-08-24: chain reconciliation starts that day' },
  { label: 'Dust / spam token',    note: 'Dust or a spam token — not a desk movement' },
  { label: "Not the desk's move",  note: "Not the desk's move — a transfer the desk neither sent nor was paid" },
];

/** The status pill: the state as a word, the tone only tints it (ChainActivity STATUS). */
export const STATUS: Record<string, { word: string; tone: PillTone }> = {
  pending: { word: 'Unmatched',       tone: 'warn' },
  auto:    { word: 'Auto-matched',    tone: 'good' },
  manual:  { word: 'Matched by hand', tone: 'info' },
  ignored: { word: 'Ignored', tone: 'neutral' },
};

/** The pill is for All, and for a row whose state is not its tab's (ChainActivity showsStatus, 21 Oct 2026). */
export const showsStatus = (tab: string, status: string): boolean => tab === 'all' || status !== tab;

/** A books entry's kind in words, when it has no party (ChainActivity KIND_WORDS). */
export const KIND_WORDS: Record<string, string> = {
  trade: 'Trade', transfer: 'Transfer', mc_pay_seller: 'Cheque · seller paid',
  mc_deliver: 'Cheque · delivered', mc_book: 'Cheque · deposit',
  opening_balance: 'Opening balance', party_payment_split: 'Payment, split',
  expense: 'Expense', equity_drawing: 'Drawing', drawing: 'Drawing',
  adjustment: 'Adjustment', chain_swap: 'Chain swap', interco: 'Inter-entity',
};

/** THE MATCHER'S NOTE, SPLIT for the register and the sheet — by ChainActivity's own noteParts, never a second grammar:
 *    lead     who and the other plain clauses, without the candidate list ("From Northern Gulf …", "covers 2 postings");
 *    guards   the clauses that stop a wrong booking, whole (ALREADY BOOKED …, Received then forwarded …);
 *    matches  the candidate clause in noteParts' words ("2 candidates, 1 exact", "3+ exact", "no candidates"), or ''.
 *  A pending row only; any other state's note is one short sentence (its reason, or who). */
const CANDIDATE_CLAUSE = /^(\d+ candidates?(, nearest 3)?: |no candidates — )/;
export function splitNote(r: TieRow): { lead: string; guards: string[]; matches: string } {
  if (!r.note) return { lead: '', guards: [], matches: '' };
  if (r.status !== 'pending') return { lead: r.note, guards: [], matches: '' };
  const clauses = r.note.split(' · ');
  const at = clauses.findIndex((c) => CANDIDATE_CLAUSE.test(c));
  const head = noteParts(at < 0 ? r.note : clauses.slice(0, at).join(' · '), r.covers?.[0]?.txns.length ?? 0);
  // "covers N postings" is said once, beside the candidate count (from r.covers), not again in the lead.
  const lead = head.plain.split(' · ').filter((c) => !/^covers \d+ postings$/.test(c)).join(' · ');
  return { lead, guards: head.guards, matches: at < 0 ? '' : noteParts(clauses.slice(at).join(' · ')).plain };
}

/** "12m before" / "3h after" — how far the books entry's time is from the transfer's. Whole units, the larger one. */
export function gapWords(entryIso: string, transferIso: string): string {
  const ms = Date.parse(entryIso) - Date.parse(transferIso);
  if (!Number.isFinite(ms)) return '';
  const m = Math.round(Math.abs(ms) / 60_000);
  if (m === 0) return 'same minute';
  const span = m < 60 ? `${m}m` : m < 48 * 60 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`;
  return `${span} ${ms < 0 ? 'before' : 'after'}`;
}

/** A GUARD SENTENCE IN ONE SHORT LINE for the row (operator's ruling, 26 Oct: safety checks stay, one line, the detail on
 *  a tap — the opened transfer shows it whole). The matcher's clause is never reworded in the detail; here it is named. */
export function guardShort(g: string): string {
  if (/^already booked/i.test(g)) {
    const n = /(\d+) times?/.exec(g)?.[1];
    // Short enough for one line beside its warning mark (Astra, 1 Nov 2026: "again" was stranded); the sheet says it whole.
    return n === '2' ? "Booked twice — don't book again" : n ? `Booked ${n} times — don't book again` : "Already booked — don't book again";
  }
  if (/^Received then forwarded/.test(g)) return 'Received then forwarded';
  if (/^Part of a pass-through/.test(g)) return 'Part of a pass-through';
  const words = g.split(/\s+/);
  return words.length <= 7 ? g : `${words.slice(0, 7).join(' ')}…`;
}

/** The venue account a send pairs with, by its Books name: "Bitget Main" (venue + venue_label), else the rail's name. One copy, for
 *  the sheet's Suggested move and the list's line under the transfer (TERMINOLOGY §2: the venue account keeps its Books name). */
export function venueAccount(r: Pick<ChainRow, 'venue' | 'venue_label' | 'venue_slug'>): string {
  return [brandCase(r.venue ?? ''), r.venue_label].filter(Boolean).join(' ') || railName({ slug: r.venue_slug ?? '' });
}
